import { hydrate, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { DashboardPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import DashboardCardStyles from '@/features/Dashboard/components/styles/DashboardCardStyles';
import {
  DASHBOARD_ROW_DEFAULT_HEIGHT,
  Dashboard2ColumnType,
} from '@/features/Dashboard/types/constants';
import { setDefaultTimeZone } from '@/services/index';
import useTagStore from '@/stores/TagStore';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import {
  DEFAULT_FEEDBACKS_SETTING,
  FeedbackGalleryView,
} from '../(dashboard)/feedback-gallery';
import { useGetStaticDashboardChartData } from './hooks';
import { getStaticDashboardPayload } from './lib/static-dashboard-storage';
import StaticDashboard2AnalysisBox from './StaticDashboard2AnalysisBox';
import StaticDashboard2Card from './StaticDashboard2Card';
import StaticDashboard2TextCard from './StaticDashboard2TextCard';
import type {
  SerializedDashboardDateFilterState,
  StaticChartItem,
  StaticDashboard2StoredData,
} from './types';

const { StyledDashboardCard, StyledDashboardCardWrapper } = DashboardCardStyles;

type StaticDashboardPageData = {
  dashboard: StaticDashboard2StoredData['dashboard'];
  tables: StaticDashboard2StoredData['tables'];
  workspaceId?: string;
  globalFilter: StructuredFilter | null;
  globalDateFilter: DashboardDateFilterState | null;
};

/**
 * @description
 * 이 페이지는 Dashboard PDF 생성을 위한 페이지로, socket server에서도 PDF 생성을 위해 호출하는 페이지입니다.
 * indexdb의 데이터 구조 수정이 필요하다면 socket server도 같이 수정이 필요합니다. (syncly-socket/src/exporter/exporter.service.ts)
 */
const StaticDashboard2Page = () => {
  const queryClient = useQueryClient();
  const data = useStaticDashboardData(queryClient);
  const { getChartData } = useGetStaticDashboardChartData(data?.tables);

  const charts = useMemo<StaticChartItem[]>(() => {
    if (!data?.dashboard.charts) return [];

    return data.dashboard.charts.flatMap((chart) => {
      const historical = chart.historical?.value ?? false;
      const params = new DashboardPreprocess({
        chart,
        historical,
        globalFilter: data.globalFilter,
        globalDateFilter: data.globalDateFilter,
      }).runPreprocess();

      const tableQueryKey = [
        'chart-table',
        JSON.stringify({
          ...params,
          name: chart.name,
        }),
      ];

      const tableData = getChartData({
        queryKey: tableQueryKey,
        chartId: chart.alias,
      });

      if (!tableData) return [];

      return [
        {
          ...chart,
          tableData,
          wordCloudData: [],
        },
      ];
    });
  }, [
    data?.dashboard.charts,
    data?.globalDateFilter,
    data?.globalFilter,
    getChartData,
  ]);

  if (!data) return <Style>Data not exist</Style>;

  return (
    <Style id="static-root">
      <Helmet>
        <title>
          Syncly Dashboard {data.dashboard.title}{' '}
          {getTimezoneDayjs().format(DATE_FORMAT.base)}
        </title>
      </Helmet>
      <Heading type="h1">{data.dashboard.title}</Heading>
      <Space h={8} />
      <Typo type="label1">
        {getTimezoneDayjs(data.dashboard.updated_at).format(DATE_FORMAT.base)}
      </Typo>
      <Space h={24} />
      <VFlex g={16}>
        {data.dashboard.rows?.map((row) => {
          return (
            <Flex
              key={row.id}
              g={16}
              style={{
                height:
                  row.columns.length === 1 &&
                  row.columns.every(
                    (col) =>
                      col.type ===
                      Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
                  )
                    ? 'auto'
                    : row.height || DASHBOARD_ROW_DEFAULT_HEIGHT,
              }}
            >
              {row.columns.map((col) => {
                switch (col.type) {
                  case Dashboard2ColumnType.DASHBOARD_COLUMN_CHART: {
                    const chart = charts.find(
                      (item) => item.alias === col.chart,
                    );
                    if (!chart) return null;

                    return (
                      <ErrorBoundary
                        key={col.id}
                        fallbackRender={() => {
                          return (
                            <CenterFlex style={{ height: '100%' }}>
                              <Typo type="label2">Error occurred</Typo>
                            </CenterFlex>
                          );
                        }}
                      >
                        <StaticDashboard2Card chart={chart} />
                      </ErrorBoundary>
                    );
                  }
                  case Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX:
                    return (
                      <StaticDashboard2TextCard key={col.id} text={col.text} />
                    );
                  case Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS: {
                    const analysis = data.dashboard.analyses?.find(
                      (item) => item.alias === col.analysis_id,
                    );
                    const chart = data.dashboard.charts?.find(
                      (item) => item.alias === analysis?.reference_id,
                    );
                    if (!analysis || !chart) return null;

                    return (
                      <StaticDashboard2AnalysisBox
                        key={col.id}
                        analysis={analysis}
                        chartName={chart.name}
                      />
                    );
                  }
                  case Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY:
                    if (!col.id) return null;

                    return (
                      <ErrorBoundary key={col.id} fallback={<></>}>
                        <StyledDashboardCardWrapper
                          className="break-inside-avoid"
                          isDragging={false}
                          width="100%"
                          style={{
                            height: '100%',
                            minWidth: 0,
                            flex: '1 1 0',
                          }}
                        >
                          <StyledDashboardCard
                            className="h-full"
                            style={{ minWidth: 0, width: '100%' }}
                          >
                            <FeedbackGalleryView
                              feedbacksSetting={
                                col.feedbacks_setting ||
                                DEFAULT_FEEDBACKS_SETTING
                              }
                              columnId={col.id}
                              queryEnabled={false}
                              workspaceId={data.workspaceId}
                              disableCardClick
                              globalFilter={data.globalFilter}
                              globalDateFilter={data.globalDateFilter}
                            />
                          </StyledDashboardCard>
                        </StyledDashboardCardWrapper>
                      </ErrorBoundary>
                    );
                  default:
                    throw new Error('Invalid column');
                }
              })}
            </Flex>
          );
        })}
      </VFlex>
    </Style>
  );
};

const useStaticDashboardData = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  const setTagMap = useTagStore((state) => state.setTagMap);
  const [data, setData] = useState<StaticDashboardPageData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const storedData: StaticDashboard2StoredData | null =
        await getStaticDashboardPayload();
      if (!storedData) {
        console.error('Data not found');
        return;
      }
      if (cancelled) return;

      if (storedData.timezone) {
        setDefaultTimeZone(storedData.timezone);
      }
      setTagMap(storedData.tagMap);
      if (storedData.queryState) {
        hydrate(queryClient, storedData.queryState);
      }
      setData({
        dashboard: storedData.dashboard,
        tables: storedData.tables,
        workspaceId: storedData.workspaceId,
        globalFilter: storedData.globalFilter ?? null,
        globalDateFilter: deserializeDashboardDateFilter(
          storedData.globalDateFilter ?? null,
        ),
      });
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [queryClient, setTagMap]);

  return data;
};

const deserializeDashboardDateFilter = (
  globalDateFilter: SerializedDashboardDateFilterState | null,
): DashboardDateFilterState | null => {
  if (!globalDateFilter) return null;

  return {
    ...globalDateFilter,
    dateRange: globalDateFilter.dateRange
      ? [
          getTimezoneDayjs(globalDateFilter.dateRange[0]),
          getTimezoneDayjs(globalDateFilter.dateRange[1]),
        ]
      : null,
  };
};

const Style = styled.div`
  padding: 40px;
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      forced-color-adjust: exact !important;
    }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

export default StaticDashboard2Page;
